import { IStore } from "@/redux/defaultStore";
import { GetUserSummaryResponse, OverwolfApi, GetOverwolfUserSummaryPeriodEnum } from "@devour/client";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import getConfig from "@/utils/getConfig";

export const OVERWOLF_SUMMARY_QUERY_KEY = "overwolf-summary";

interface UseGetOverwolfSummaryOptions {
    enabled?: boolean;
    period?: "today" | "weekly" | "monthly" | "allTime";
}

async function fetchOverwolfSummary(fullToken: ReturnType<typeof getConfig>, period: string): Promise<GetUserSummaryResponse> {
    return new OverwolfApi(fullToken).getOverwolfUserSummary({
        period: period as GetOverwolfUserSummaryPeriodEnum,
    });
}

export const useGetOverwolfSummary = (options: UseGetOverwolfSummaryOptions = {}) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const period = options.period || "allTime";

    /*
     * Note: We intentionally fetch for ALL logged-in users, not just Overwolf users.
     * This is needed because isDevourPlayUser determines whether to show download CTAs,
     * and web users who have previously used DevourPlay shouldn't see the download prompt.
     */
    const { data, isLoading, refetch } = useQuery<GetUserSummaryResponse>({
        queryKey: [
            OVERWOLF_SUMMARY_QUERY_KEY,
            fullToken?.id,
            period,
        ],
        queryFn: () => {
            if (!fullToken?.token) throw new Error("Missing authentication token");
            return fetchOverwolfSummary(getConfig(fullToken), period);
        },
        staleTime: 60000,
        enabled: !!fullToken?.token && (options.enabled ?? true),
        refetchOnWindowFocus: false,
    });

    return {
        data,
        isLoading,
        refetch,
    };
};
